/**
 * api.ts — Thin fetch wrapper para o backend Laravel (Sanctum SPA)
 *
 * Uso: importe `apiFetch` e use exatamente como `fetch`, passando apenas
 * o path a partir de /api (sem o domínio). O wrapper cuida de:
 *   - Prefixar VITE_API_URL
 *   - Enviar credentials (cookie de sessão Sanctum)
 *   - Incluir X-XSRF-TOKEN em requests mutantes (CSRF)
 *   - Definir Accept / Content-Type automaticamente
 *   - Redirecionar para /login em 401 (sessão expirada)
 *
 * Exemplo:
 *   const res = await apiFetch('/api/employees?page=1')
 *   const res = await apiFetch('/api/employees', { method: 'POST', body: JSON.stringify(data) })
 *
 * ── Diferenças em relação à versão Next.js ──────────────────────────────────
 * Este build é 100% client-side, então não existe mais `server-api.ts`: o
 * Referer/Origin que o Sanctum exige passa a ser enviado pelo próprio browser.
 * O backend precisa ter este domínio em SANCTUM_STATEFUL_DOMAINS e permitir
 * credentials no CORS (`supports_credentials => true`).
 */

export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

/** Lê o cookie XSRF-TOKEN definido pelo Laravel após /sanctum/csrf-cookie */
function getCsrfToken(): string | undefined {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * Garante que o cookie XSRF-TOKEN existe antes de requests mutantes.
 * Chamado uma vez no bootstrap do app (main.tsx) e na página de login.
 */
export async function ensureCsrf(): Promise<void> {
  if (typeof document === 'undefined') return
  if (!getCsrfToken()) {
    await fetch(`${API_URL}/sanctum/csrf-cookie`, { credentials: 'include' })
  }
}

/**
 * Rotas em que um 401 é resposta esperada e NÃO deve disparar o redirect
 * automático — senão a página de login entra em loop de reload.
 */
const NO_REDIRECT_ON_401 = ['/api/auth/me', '/api/auth/login', '/api/auth/logout']

/**
 * Substituto direto para `fetch('/api/...')`.
 * Basta trocar fetch( por apiFetch( — tudo mais permanece igual.
 */
export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const method = ((init.method as string) || 'GET').toUpperCase()
  const isFormData = init.body instanceof FormData
  const needsCsrf = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)
  const csrf = needsCsrf ? getCsrfToken() : undefined

  const headers: Record<string, string> = {
    Accept: 'application/json',
  }

  // Content-Type automático para JSON (não FormData)
  if (!isFormData && init.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  // CSRF obrigatório para requests mutantes
  if (csrf) {
    headers['X-XSRF-TOKEN'] = csrf
  }

  // Headers do chamador têm precedência
  const mergedHeaders = { ...headers, ...(init.headers as Record<string, string> | undefined) }

  const request = fetch(`${API_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: mergedHeaders,
  })

  // Redireciona automaticamente para /login em 401 (sessão expirada)
  return request.then(res => {
    const isAuthRoute = NO_REDIRECT_ON_401.some(p => path.startsWith(p))
    if (res.status === 401 && !isAuthRoute && typeof window !== 'undefined') {
      const here = window.location.pathname + window.location.search
      window.location.href = `/login?callbackUrl=${encodeURIComponent(here)}`
    }
    return res
  })
}

/**
 * Helper: faz logout no Laravel e redireciona para /login.
 * Use no botão "Sair" do header.
 */
export async function logout(): Promise<void> {
  await apiFetch('/api/auth/logout', { method: 'POST' }).catch(() => {})
  if (typeof window !== 'undefined') {
    // Hard navigation de propósito: descarta todo o cache do TanStack Query
    // e qualquer estado em memória junto com a sessão.
    window.location.href = '/login'
  }
}

/**
 * Extrai mensagem de erro amigável de respostas Laravel.
 *
 * Laravel retorna erros de validação no formato:
 *   { message: "The given data was invalid.", errors: { campo: ["mensagem 1", ...] } }
 *
 * Esta função retorna os erros de validação campo a campo, separados por " · ".
 * Se não houver `errors`, retorna `message`. Caso contrário usa o fallback.
 */
export function extractApiError(data: any, fallback = 'Erro ao processar requisição.'): string {
  if (!data) return fallback
  if (data.errors && typeof data.errors === 'object') {
    const msgs = Object.values(data.errors as Record<string, string[]>).flat()
    if (msgs.length > 0) return msgs.join(' · ')
  }
  return data.message || data.error || fallback
}

/**
 * GET + json() em uma chamada, com erro tipado. É a base dos `queryFn` do
 * TanStack Query — em caso de falha lança `ApiError`, que o Query trata como
 * estado de erro (em vez de resolver com um objeto vazio silenciosamente).
 */
export class ApiError extends Error {
  status: number
  data: unknown

  constructor(message: string, status: number, data: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

export async function apiGet<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(path, init)
  const data = await res.json().catch(() => null)
  if (!res.ok) {
    throw new ApiError(extractApiError(data, 'Erro ao carregar dados.'), res.status, data)
  }
  return data as T
}

/** Mesmo contrato do `apiGet`, para requests mutantes. */
export async function apiSend<T>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const res = await apiFetch(path, {
    method,
    body:
      body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
  })
  const data = await res.json().catch(() => null)
  if (!res.ok) {
    throw new ApiError(extractApiError(data), res.status, data)
  }
  return data as T
}
